import { defineStore } from 'pinia';
import type { SettingsState } from './settings/settings.model';
import {
	readAdvancedFilterCollapse,
	readAdvancedFilterLayouts,
	readDataListPreferences,
	readFilesTablePreferences,
	readSplitRouteMemory,
	readSplitPanelWidths,
	resolveInitialCustomFieldsManagerShowDefaultFields,
} from './settings/settings.utils';
import { uiPreferencesActions } from './settings/actions/uiPreferences.actions';
import { dataListPreferencesActions } from './settings/actions/dataListPreferences.actions';
import { filesTablePreferencesActions } from './settings/actions/filesTablePreferences.actions';
import { selectionMemoryActions } from './settings/actions/selectionMemory.actions';
import { layoutPreferencesActions } from './settings/actions/layoutPreferences.actions';

export {
	CUSTOM_FIELDS_MANAGER_DEFAULT_FIELDS_VISIBLE_STORAGE_KEY,
	DATA_LIST_PREFERENCES_STORAGE_KEY,
	FILES_TABLE_PREFERENCES_STORAGE_KEY,
	SPLIT_PANEL_WIDTHS_STORAGE_KEY,
	ADVANCED_FILTER_LAYOUTS_STORAGE_KEY,
	ADVANCED_FILTER_COLLAPSE_STORAGE_KEY,
	SPLIT_ROUTE_MEMORY_STORAGE_KEY,
} from './settings/settings.constants';

export type {
	ColorMode,
	AdvancedFilterLayoutMode,
	DataListSortPreference,
	FilesTableSortPreference,
	FilesTableSortDirection,
	FilesTableSortState,
	FilesTableMetaItemKey,
	FilesTableMetaVisibility,
	SplitRouteMemoryEntry,
	UIPreferencesPayload,
	UserPreferenceBasePayload,
} from './settings/settings.model';

/**
 * Global UI settings store.
 *
 * The store remains a single source of truth, while implementation is split
 * by feature category in `src/stores/settings/actions/*`.
 */
export const useSettingsStore = defineStore('settings', {
	state: (): SettingsState => ({
		colorMode: 'light',
		splitViewEnabled: false,
		customFieldsManagerShowDefaultFields:
			resolveInitialCustomFieldsManagerShowDefaultFields(),
		dataListPreferences: readDataListPreferences(),
		filesTablePreferences: readFilesTablePreferences(),
		dataListSelectionMemory: {},
		dataListSelectionPanelMemory: {},
		splitPanelWidths: readSplitPanelWidths(),
		advancedFilterLayouts: readAdvancedFilterLayouts(),
		advancedFilterCollapse: readAdvancedFilterCollapse(),
		splitRouteMemory: readSplitRouteMemory(),
		isApplyingUIPreferences: false,
	}),
	getters: {
		isDarkMode: (state): boolean => state.colorMode === 'dark',
	},
	actions: {
		...uiPreferencesActions,
		...dataListPreferencesActions,
		...filesTablePreferencesActions,
		...selectionMemoryActions,
		...layoutPreferencesActions,
	},
});
